// Resumen: las cuentas del vertical con su actividad (último Gong, deal SF,
// notas), botones de sincronización manual y alta de cuentas.

import { useMemo, useState } from 'react'
import { formatDistanceToNow } from 'date-fns'
import { es } from 'date-fns/locale'
import { Building2, Phone, Plus, RefreshCw, TrendingUp } from 'lucide-react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { usePermissions } from '@/hooks/usePermissions'
import { useBrainPages, useBrainSync } from '@/hooks/useBrain'
import type { BrainPageMeta } from '@/types/brain'
import { AddAccountDialog } from './AddAccountDialog'
import {
  ROLLOUT_START,
  TERMINAL_MIGRATION_CLOSE,
  ROLLOUT_RAMP,
  EXIT_MONTHLY_TX,
  EXIT_ARR_USD,
  EXIT_PCT_OF_ARR,
  compactTx,
  millionsUsd,
} from '@/workshops-bc/lib/inperson-math'

// Calendario del proyecto — MISMA fuente que el deck C-Level (inperson-math),
// nada duplicado a mano.
const TIMELINE_PHASES = [
  { q: TERMINAL_MIGRATION_CLOSE, label: 'Desarrollo de producto · cierra la migración a Ingenico' },
  { q: 'Q1 2027', label: 'Tests sandbox + productivos · partner de terminales en paralelo' },
  { q: ROLLOUT_START, label: `Rollout ${compactTx(ROLLOUT_RAMP[0].tx)} → ${compactTx(ROLLOUT_RAMP[2].tx)} tx/mes` },
  { q: ROLLOUT_RAMP[ROLLOUT_RAMP.length - 1].quarter, label: `+1M/mes hasta ${compactTx(EXIT_MONTHLY_TX)} tx/mes · run-rate ${millionsUsd(EXIT_ARR_USD, 2)}M ARR (${EXIT_PCT_OF_ARR}% del deal)` },
]

interface Props {
  onOpenPage: (slug: string) => void
}

interface AccountView {
  company: BrainPageMeta
  lastMeeting: BrainPageMeta | null
  openDeal: BrainPageMeta | null
  meetingCount: number
  dealCount: number
}

export function ResumenTab({ onOpenPage }: Props) {
  // Companies and deals come from dedicated queries: the mixed 200-row recency
  // window would silently drop them once daily syncs pile up meetings/notes.
  // Meetings stay windowed (labels say 'recientes').
  const { data: companyPages, isLoading } = useBrainPages({ pageType: 'company', limit: 100 })
  const { data: dealPages } = useBrainPages({ pageType: 'deal', limit: 100 })
  const { data: recentPages } = useBrainPages()
  const syncGong = useBrainSync('gong')
  const syncSf = useBrainSync('sf')
  const syncFireflies = useBrainSync('fireflies')
  // brain-write gates company pages a admin/manager — no mostrar botones que
  // el backend va a rechazar con 403.
  const { role } = usePermissions()
  const canEditAccounts = role === 'admin' || role === 'manager'
  const canSync = role !== 'viewer' // el backend rechaza viewers con 403
  const [addOpen, setAddOpen] = useState(false)
  const [editing, setEditing] = useState<BrainPageMeta | null>(null)

  const accounts = useMemo<AccountView[]>(() => {
    const all = recentPages ?? []
    const fm = (p: BrainPageMeta) => (p.frontmatter ?? {}) as Record<string, unknown>
    // 'Última llamada' se ordena por la FECHA de la llamada, no por updated_at
    // (un backfill/re-sync de una llamada vieja no debe volverla 'reciente').
    const meetingDate = (p: BrainPageMeta) => String(fm(p).call_date ?? p.updated_at)
    return (companyPages ?? []).map((company) => {
      const meetings = all
        .filter((p) => p.page_type === 'meeting' && fm(p).company === company.slug)
        .sort((a, b) => meetingDate(b).localeCompare(meetingDate(a)))
      const deals = (dealPages ?? []).filter((p) => fm(p).account_slug === company.slug)
      const openDeals = deals.filter((d) => !fm(d).is_closed)
      return {
        company,
        lastMeeting: meetings[0] ?? null,
        openDeal: openDeals[0] ?? deals[0] ?? null,
        meetingCount: meetings.length,
        dealCount: deals.length,
      }
    })
  }, [companyPages, dealPages, recentPages])

  const runSync = async (kind: 'gong' | 'sf' | 'fireflies') => {
    const m = kind === 'gong' ? syncGong : kind === 'fireflies' ? syncFireflies : syncSf
    try {
      const res = await m.mutateAsync()
      const org = res.orgs?.[0] as Record<string, unknown> | undefined
      const label = kind === 'gong' ? 'Gong' : kind === 'fireflies' ? 'Fireflies' : 'Salesforce'
      const partialErrors = Array.isArray(org?.errors) ? (org!.errors as string[]) : []
      if (org?.skipped) toast.info(`Sync ${label}: ${String(org.skipped)}`)
      else if (org?.error) toast.error(`Sync falló: ${String(org.error)}`)
      else if (partialErrors.length) toast.warning(`Sync ${label} completado con ${partialErrors.length} error${partialErrors.length === 1 ? '' : 'es'}`)
      else toast.success(`Sync ${label} completado`)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Sync falló')
    }
  }

  // Defensivo: call_date viene de Gong sin validar — una fecha malformada no
  // debe tirar el tab entero a RangeError.
  const ago = (iso: string) => {
    const d = new Date(iso)
    return Number.isNaN(d.getTime()) ? '—' : formatDistanceToNow(d, { addSuffix: true, locale: es })
  }

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Calendario del proyecto
        </div>
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {TIMELINE_PHASES.map((phase, i) => (
            <div key={phase.q} className="relative rounded-lg border border-border/60 p-3">
              <div className="flex items-center gap-2">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-teal-500/15 text-[10px] font-bold text-teal-600 dark:text-teal-400">
                  {i + 1}
                </span>
                <span className="font-heading text-sm font-semibold">{phase.q}</span>
              </div>
              <p className="mt-1.5 text-xs text-muted-foreground">{phase.label}</p>
            </div>
          ))}
        </div>
      </Card>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          {accounts.length} cuenta{accounts.length === 1 ? '' : 's'} en el vertical · sincroniza las
          llamadas y el pipeline con los botones (el cron diario se activa con la mig 186)
        </p>
        <div className="flex gap-2">
          {canSync && (<>
          <Button variant="outline" size="sm" onClick={() => runSync('gong')} disabled={syncGong.isPending}>
            <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${syncGong.isPending ? 'animate-spin' : ''}`} />
            Sync Gong
          </Button>
          <Button variant="outline" size="sm" onClick={() => runSync('sf')} disabled={syncSf.isPending}>
            <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${syncSf.isPending ? 'animate-spin' : ''}`} />
            Sync Salesforce
          </Button>
          <Button variant="outline" size="sm" onClick={() => runSync('fireflies')} disabled={syncFireflies.isPending}>
            <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${syncFireflies.isPending ? 'animate-spin' : ''}`} />
            Sync Fireflies
          </Button>
          </>)}
          {canEditAccounts && (
            <Button size="sm" onClick={() => { setEditing(null); setAddOpen(true) }}>
              <Plus className="mr-1.5 h-3.5 w-3.5" /> Agregar cuenta
            </Button>
          )}
        </div>
      </div>

      {isLoading && <div className="py-10 text-center text-sm text-muted-foreground">Cargando cuentas…</div>}

      {!isLoading && accounts.length === 0 && (
        <Card className="flex flex-col items-center gap-3 p-10 text-center">
          <Building2 className="h-8 w-8 text-muted-foreground" />
          <div className="text-sm font-medium">Todavía no hay cuentas en el vertical</div>
          <p className="max-w-sm text-sm text-muted-foreground">
            Agrega BanCoppel, Quálitas, McDonald's y Redeban para que el cerebro empiece a conectar
            sus llamadas de Gong y su pipeline de Salesforce.
          </p>
          {canEditAccounts && (
            <Button size="sm" onClick={() => { setEditing(null); setAddOpen(true) }}>
              <Plus className="mr-1.5 h-3.5 w-3.5" /> Agregar la primera cuenta
            </Button>
          )}
        </Card>
      )}

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {accounts.map(({ company, lastMeeting, openDeal, meetingCount, dealCount }) => {
          const fm = (company.frontmatter ?? {}) as Record<string, unknown>
          const dealFm = (openDeal?.frontmatter ?? {}) as Record<string, unknown>
          return (
            <Card key={company.id} className="flex flex-col gap-2 p-4">
              <div className="flex items-start justify-between gap-2">
                <button
                  type="button"
                  onClick={() => onOpenPage(company.slug)}
                  className="text-left font-heading text-base font-semibold hover:underline"
                >
                  {company.title}
                </button>
                <div className="flex items-center gap-1">
                  {typeof fm.country === 'string' && fm.country && (
                    <Badge variant="outline">{fm.country}</Badge>
                  )}
                  {canEditAccounts && (
                    <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => { setEditing(company); setAddOpen(true) }}>
                      Editar
                    </Button>
                  )}
                </div>
              </div>
              {typeof fm.status === 'string' && fm.status && (
                <div className="text-xs text-muted-foreground">{fm.status}</div>
              )}
              <div className="mt-1 space-y-1.5 text-sm">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-3.5 w-3.5 shrink-0 text-emerald-500" />
                  {openDeal ? (
                    <button type="button" className="truncate text-left hover:underline" onClick={() => onOpenPage(openDeal.slug)}>
                      {String(dealFm.stage ?? 'Oportunidad')}
                      {dealFm.amount != null ? ` · ${String(dealFm.currency ?? 'USD')} $${Math.round(Number(dealFm.amount)).toLocaleString('en-US')}` : ''}
                      {typeof dealFm.next_step === 'string' && dealFm.next_step ? ` · ${dealFm.next_step}` : ''}
                    </button>
                  ) : (
                    <span className="text-muted-foreground">
                      {fm.sf_account_id ? 'Sin oportunidades sincronizadas' : 'Sin Account Id de SF'}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="h-3.5 w-3.5 shrink-0 text-amber-500" />
                  {lastMeeting ? (
                    <button type="button" className="truncate text-left hover:underline" onClick={() => onOpenPage(lastMeeting.slug)}>
                      {lastMeeting.title} · {ago(String((lastMeeting.frontmatter as Record<string, unknown>)?.call_date ?? lastMeeting.updated_at))}
                    </button>
                  ) : (
                    <span className="text-muted-foreground">Sin llamadas recientes de Gong</span>
                  )}
                </div>
              </div>
              <div className="mt-auto flex gap-3 pt-2 text-xs text-muted-foreground">
                <span>{meetingCount} llamada{meetingCount === 1 ? '' : 's'} reciente{meetingCount === 1 ? '' : 's'}</span>
                <span>{dealCount} oportunidad{dealCount === 1 ? '' : 'es'}</span>
              </div>
            </Card>
          )
        })}
      </div>

      <AddAccountDialog open={addOpen} onOpenChange={setAddOpen} editing={editing} />
    </div>
  )
}
